import React, { useEffect, useMemo } from 'react';
import { ButtonBase, Chip, Paper, Stack, Typography, Box, alpha } from '@mui/material';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { useCheckoutActions, useCheckoutState } from '../store';
import { QtyStepper } from './QtyStepper';
import { CancelConfirmModal } from './CancelConfirmModal';
import { CancelLinkRow } from './CancelLinkRow';
import { CHIP_OVERLINE_SX, SECTION_LABEL_SX } from './sharedStyles';
import { useCancelSubscription } from './useCancelSubscription';
import {
  CHECKOUT_COPY,
  CHECKOUT_PRICING,
  CYCLE_SUFFIX,
  PRICE_VARIANTS,
  PRODUCTS,
  isCancelableSubscription,
  subscriptionBillingCycle,
  subscriptionLineItems,
  lineItemProduct
} from 'lib/billing';
import { formatCents } from '../format';

type TierKey = 'standard' | 'premium' | 'futures';

const TIER_DEFS: { key: TierKey; field: string; setter: string; product: string }[] = [
  { key: 'standard', field: 'standardCount', setter: 'setStandardCount', product: PRODUCTS.standard },
  { key: 'premium', field: 'premiumCount', setter: 'setPremiumCount', product: PRODUCTS.premium },
  { key: 'futures', field: 'futuresCount', setter: 'setFuturesCount', product: PRODUCTS.futures }
];

function TierCancelControl({
  subscription,
  subscriptionItemId,
  productLabel
}: {
  subscription: any;
  subscriptionItemId: string | null;
  productLabel: string;
}) {
  const isLineRemoval = Boolean(subscriptionItemId);
  const copy = isLineRemoval ? CHECKOUT_COPY.cancel.tierLine : CHECKOUT_COPY.cancel.tier;
  const cancel = useCancelSubscription({
    subscriptionId: subscription.id,
    subscriptionItemId: subscriptionItemId || undefined,
    subscription,
    successMessage: copy.success,
    errorMessage: copy.error
  });

  if (!isCancelableSubscription(subscription)) return null;

  return (
    <>
      <CancelLinkRow label={copy.buttonLabel} busy={cancel.busy} onClick={cancel.openModal} boxSx={{ ml: '26px', mt: 1.25 }} />
      <CancelConfirmModal
        open={cancel.open}
        onClose={cancel.close}
        onConfirm={cancel.run}
        busy={cancel.busy}
        title={copy.title(productLabel)}
        description={copy.description}
        confirmLabel={copy.confirmLabel}
      />
    </>
  );
}

// Pay-as-you-go vs Unlimited selector, shown ABOVE the futures card (only when
// the catalog stocks both SKUs for the cycle and a trial isn't forcing tiered).
function FuturesVariantToggle({
  unlimited,
  onChange,
  includedBase
}: {
  unlimited: boolean;
  onChange: (v: boolean) => void;
  includedBase: number;
}) {
  const COPY = CHECKOUT_COPY.configure;
  const options = [
    { unlimited: false, title: COPY.futuresModePayg, note: `${includedBase} included` },
    { unlimited: true, title: COPY.futuresModeUnlimited, note: COPY.futuresModeUnlimitedNote }
  ];
  return (
    <Box
      role="radiogroup"
      aria-label={COPY.futuresModeLabel}
      sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, pb: 1.5, mb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
    >
      {options.map((opt) => {
        const selected = opt.unlimited === unlimited;
        return (
          <ButtonBase
            key={String(opt.unlimited)}
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(opt.unlimited)}
            sx={(theme) => ({
              flexDirection: 'column',
              alignItems: 'flex-start',
              textAlign: 'left',
              px: 1.5,
              py: 1,
              borderRadius: 2,
              border: '1px solid',
              borderColor: selected ? 'primary.main' : 'divider',
              bgcolor: selected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
              transition: 'border-color 150ms ease, background 150ms ease',
              '&:hover': selected ? {} : { borderColor: 'primary.light' }
            })}
          >
            <Typography variant="body2" fontWeight={700} color={selected ? 'primary.main' : 'text.primary'}>
              {opt.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {opt.note}
            </Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );
}

// Read-only status pill used for Unlimited (flat SKU, no count) and the
// trial-locked tiered bundle (count fixed to the included base).
function StatusPill({ children, tone = 'neutral' }: { children: React.ReactNode; tone?: 'neutral' | 'primary' }) {
  return (
    <Box
      sx={(theme) => ({
        display: 'inline-flex',
        alignItems: 'center',
        px: 1.5,
        height: 36,
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        color: tone === 'primary' ? 'primary.main' : 'text.primary',
        bgcolor: tone === 'primary' ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.text.primary, 0.05)
      })}
    >
      {children}
    </Box>
  );
}

function AddButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1.75,
        height: 36,
        borderRadius: 999,
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        fontSize: 12.5,
        fontWeight: 600,
        boxShadow: '0 4px 12px -4px rgba(0,0,0,0.3)',
        transition: 'transform 120ms ease, box-shadow 120ms ease',
        '&:hover': { boxShadow: '0 6px 16px -6px rgba(0,0,0,0.35)' },
        '&:active': { transform: 'scale(0.96)' }
      }}
    >
      <PlusOutlined style={{ fontSize: 12 }} /> {label}
    </ButtonBase>
  );
}

function RemoveButton({ onClick }: { onClick: () => void }) {
  return (
    <ButtonBase
      onClick={onClick}
      aria-label="Remove Futures"
      sx={(theme) => ({
        width: 28,
        height: 28,
        borderRadius: '50%',
        color: 'text.secondary',
        transition: 'color 120ms ease, background 120ms ease, transform 120ms ease',
        '&:hover': { color: 'error.main', bgcolor: alpha(theme.palette.error.main, 0.1) },
        '&:active': { transform: 'scale(0.9)' }
      })}
    >
      <CloseOutlined style={{ fontSize: 12 }} />
    </ButtonBase>
  );
}

function TierCard({
  tierKey,
  count,
  perCycleCents,
  perExtraCents,
  cycleSuffix,
  onChange,
  isCurrentPlan,
  ownedSubscription,
  ownedSubscriptionItemId,
  minCount,
  futuresEffectiveUnlimited,
  futuresIncludedBase,
  futuresCycleCents,
  showFuturesToggle,
  onFuturesUnlimitedChange,
  onFuturesRemove
}: any) {
  const meta = (CHECKOUT_COPY.configure.tiers as any)[tierKey];
  const showFutures = tierKey === 'futures';
  const owned = Boolean(ownedSubscription?.id);
  const active = showFutures && futuresEffectiveUnlimited ? true : count > 0;
  const hasCancel = owned;

  // Futures control: Unlimited pill, trial-locked included pill, plain stepper
  // for an existing line, floored stepper + remove for a new tiered selection,
  // or an Add button when futures is off.
  let control: React.ReactNode;
  if (showFutures) {
    if (futuresEffectiveUnlimited) {
      // Unlimited is a flat SKU (no stepper). Still allow deselecting it when
      // it's a new selection — owned subs use the cancel link below instead.
      control = (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <StatusPill>{CHECKOUT_COPY.configure.futuresUnlimitedBadge}</StatusPill>
          {!owned && <RemoveButton onClick={onFuturesRemove} />}
        </Stack>
      );
    } else if (owned) {
      control = <QtyStepper value={count} onChange={onChange} ariaLabel={`${meta.name} accounts`} min={minCount || 1} />;
    } else if (count > 0) {
      control = (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <QtyStepper value={count} onChange={onChange} ariaLabel={`${meta.name} accounts`} min={futuresIncludedBase} />
          <RemoveButton onClick={onFuturesRemove} />
        </Stack>
      );
    } else {
      control = <AddButton onClick={() => onChange(futuresIncludedBase)} label={CHECKOUT_COPY.configure.addLabel} />;
    }
  } else {
    control = <QtyStepper value={count} onChange={onChange} ariaLabel={`${meta.name} accounts`} min={minCount || 0} />;
  }

  const description = showFutures
    ? futuresEffectiveUnlimited
      ? CHECKOUT_COPY.configure.futuresUnlimitedSummary
      : `${futuresIncludedBase} included · add extras anytime`
    : meta.description;

  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        px: 2,
        pt: 2,
        pb: hasCancel ? 1 : 2,
        borderRadius: 2,
        bgcolor: active ? alpha(theme.palette.primary.main, 0.03) : 'background.paper',
        borderColor: isCurrentPlan ? 'primary.main' : active ? alpha(theme.palette.primary.main, 0.25) : 'divider',
        transition: 'border-color 200ms ease, background 200ms ease'
      })}
    >
      {showFutures && showFuturesToggle && (
        <FuturesVariantToggle
          unlimited={futuresEffectiveUnlimited}
          onChange={onFuturesUnlimitedChange}
          includedBase={futuresIncludedBase}
        />
      )}
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: active ? 'primary.main' : 'divider',
            flexShrink: 0
          }}
        />
        <Stack spacing={0.4} sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
            <Typography variant="body2" fontWeight={700}>
              {meta.name}
            </Typography>
            {isCurrentPlan && (
              <Chip size="small" label={CHECKOUT_COPY.configure.currentPlanBadge} color="success" variant="filled" sx={CHIP_OVERLINE_SX} />
            )}
            {!isCurrentPlan && meta.badge && (
              <Chip size="small" label={meta.badge} color="primary" variant="outlined" sx={CHIP_OVERLINE_SX} />
            )}
            {showFutures && futuresEffectiveUnlimited && (
              <Chip
                size="small"
                label={CHECKOUT_COPY.configure.futuresUnlimitedBadge}
                color="primary"
                variant="filled"
                sx={CHIP_OVERLINE_SX}
              />
            )}
          </Stack>
          <Typography variant="body2" fontWeight={700} color="primary.main">
            {showFutures && futuresEffectiveUnlimited
              ? `${formatCents(futuresCycleCents)}${cycleSuffix}`
              : showFutures
              ? `${CHECKOUT_COPY.configure.futuresPrefix}${formatCents(perCycleCents)}${cycleSuffix}`
              : /* Standard/Premium: perCycleCents is the per-account unit
                   (std/premUnitCycleCents), so name the unit — unqualified
                   "$10.00/mo" on a card titled "Standard" reads as the plan
                   price, and the stepper then multiplies it. Futures keeps its
                   "From" prefix: it is a graduated bundle, not per account. */
                `${formatCents(perCycleCents)}${cycleSuffix}${CHECKOUT_COPY.perAccountSuffix}`}
            {showFutures && !futuresEffectiveUnlimited && perExtraCents > 0 && (
              <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500, ml: 0.5 }}>
                {CHECKOUT_COPY.configure.futuresExtraPrefix}
                {formatCents(perExtraCents)}
                {cycleSuffix}
                {CHECKOUT_COPY.configure.futuresExtraSuffix}
              </Box>
            )}
          </Typography>
          {description ? (
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45 }}>
              {description}
            </Typography>
          ) : null}
        </Stack>
        {control}
      </Stack>
      {owned && (
        <TierCancelControl subscription={ownedSubscription} subscriptionItemId={ownedSubscriptionItemId} productLabel={meta.name} />
      )}
    </Paper>
  );
}

export function Tiers() {
  const state = useCheckoutState();
  const actions = useCheckoutActions();
  const billingCycle = state.billingCycle;
  const catalog = state.catalog;
  const cycleSuffix = (billingCycle && (CYCLE_SUFFIX as any)[billingCycle]) || '/mo';

  // Map each tier product → owner sub/line/lineCount on current cycle.
  const productToOwner = useMemo(() => {
    const tierProducts = new Set(TIER_DEFS.map((t) => t.product));
    const map = new Map<string, { sub: any; line: any; lineCount: number }>();
    for (const sub of state.activeSubscriptions || []) {
      if (subscriptionBillingCycle(sub) !== billingCycle) continue;
      const lines = subscriptionLineItems(sub);
      const lineCount = lines.length || 1;
      for (const line of lines) {
        const product = lineItemProduct(line, catalog, sub);
        if (!product || !tierProducts.has(product) || map.has(product)) continue;
        map.set(product, { sub, line, lineCount });
      }
    }
    return map;
  }, [state.activeSubscriptions, billingCycle, catalog]);

  const COPY = state.isModifyingExisting ? CHECKOUT_COPY.configureModify : CHECKOUT_COPY.configure;

  // Accounts shipped in the tiered base fee. Prefer the live catalog number
  // (first tier `up_to`) so the "N included" copy + stepper floor track the
  // backend; fall back to config when the catalog hasn't loaded.
  const futuresIncludedBase = useMemo(() => {
    const price = catalog?.get?.(PRODUCTS.futures, billingCycle);
    const upTo = price?.tiers?.[0]?.up_to ?? price?.tiers?.[0]?.upTo;
    const n = Number(upTo);
    if (Number.isFinite(n) && n > 1) return n;
    return CHECKOUT_PRICING.futures.tieredIncludedAccounts ?? 20;
  }, [catalog, billingCycle]);

  // Both SKUs must exist on this cycle for the variant toggle to make sense.
  const bothFuturesVariants = useMemo(
    () =>
      Boolean(
        catalog?.has?.(PRODUCTS.futures, billingCycle, PRICE_VARIANTS.default) &&
          catalog?.has?.(PRODUCTS.futures, billingCycle, PRICE_VARIANTS.unlimited)
      ),
    [catalog, billingCycle]
  );

  const effectiveFuturesUnlimited = Boolean((state as any).effectiveFuturesUnlimited);
  // Futures is never count-locked now: the stepper stays editable so accounts
  // can always be added. The trial constraint (covers only the included bundle)
  // is surfaced on the trial choice instead — the "Free trial" option greys out
  // once the count exceeds the included base. Toggle shown whenever both SKUs exist.
  const showFuturesToggle = bothFuturesVariants;

  const setFuturesCount = (actions as any).setFuturesCount as (n: number) => void;
  const setFuturesUnlimited = (actions as any).setFuturesUnlimited as (v: boolean) => void;

  // Tiered Futures is sold as a bundle that includes `futuresIncludedBase`
  // accounts. Snap any in-use count below the floor (e.g. the legacy default of
  // 1) up to the included base so the card + Stripe quantity match the bundle.
  // Skipped while modifying — an existing line's count is the real Stripe qty.
  const futuresCount = Number((state as any).futuresCount) || 0;
  useEffect(() => {
    if (state.isModifyingExisting) return;
    if (effectiveFuturesUnlimited) return;
    if (futuresCount > 0 && futuresCount < futuresIncludedBase) {
      setFuturesCount(futuresIncludedBase);
    }
  }, [state.isModifyingExisting, effectiveFuturesUnlimited, futuresCount, futuresIncludedBase, setFuturesCount]);

  return (
    <Stack spacing={1.5}>
      {state.isModifyingExisting && (COPY as any).tiersNote && (
        <Typography variant="caption" color="text.secondary">
          {(COPY as any).tiersNote}
        </Typography>
      )}
      <Stack spacing={1.5}>
        {TIER_DEFS.map(({ key, field, setter, product }) => {
          const owner = productToOwner.get(product) || null;
          const count = (state as any)[field] || 0;
          const perCycleKey =
            key === 'standard' ? 'stdUnitCycleCents' : key === 'premium' ? 'premUnitCycleCents' : 'futuresFirstCycleCents';
          const perCycleCents = (state as any)[perCycleKey] || 0;
          const perExtraCents = (state as any).futuresExtraCycleCents || 0;
          const isCurrentPlan = Boolean(state.activeProductLines?.has?.(key));
          const isFutures = key === 'futures';

          const card = (
            <TierCard
              tierKey={key}
              count={count}
              perCycleCents={perCycleCents}
              perExtraCents={perExtraCents}
              cycleSuffix={cycleSuffix}
              isCurrentPlan={isCurrentPlan}
              onChange={(n: number) => (actions as any)[setter]?.(n)}
              ownedSubscription={owner?.sub ?? null}
              ownedSubscriptionItemId={owner && owner.lineCount > 1 ? owner.line.subscriptionItemId : null}
              minCount={owner ? 1 : 0}
              futuresEffectiveUnlimited={isFutures && effectiveFuturesUnlimited}
              futuresIncludedBase={futuresIncludedBase}
              futuresCycleCents={(state as any).futuresCycleCents || 0}
              showFuturesToggle={isFutures && showFuturesToggle}
              onFuturesUnlimitedChange={setFuturesUnlimited}
              onFuturesRemove={(actions as any).removeFutures}
            />
          );

          const groupHeader = key === 'standard' ? 'Copier · CFD' : key === 'futures' ? 'Copier · Futures' : null;
          return (
            <React.Fragment key={key}>
              {groupHeader && (
                <Typography variant="overline" sx={SECTION_LABEL_SX}>
                  {groupHeader}
                </Typography>
              )}
              {card}
            </React.Fragment>
          );
        })}
      </Stack>
    </Stack>
  );
}
